import type { FC } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import {
	NoteActionsWrapper,
	ArchivePageActionLink,
	HorizontalSeparator,
} from './archivePageBannerNoteSharedStyles.tsx';
import { NoteViewState } from './NoteViewState.ts';

export const i18n = defineMessages({
	showMore: {
		id: 'archive-pages.page-banner.note.actions.show-more',
		defaultMessage: 'Show more',
		description:
			"button text that allows the user to expand a long message so it's no longer truncated",
	},
	showLess: {
		id: 'archive-pages.page-banner.note.actions.show-less',
		defaultMessage: 'Show less',
		description:
			'button text that allows the user to truncate an expanded message so it takes up less space',
	},
	editNote: {
		id: 'archive-pages.page-banner.note.actions.edit-note',
		defaultMessage: 'Edit note',
		description:
			'button text that allows the user to change the descriptive note on an archived page',
	},
});

type ArchivedPageBannerNoteActionsProps = {
	noteViewState: NoteViewState;
	setNoteViewState: (newState: NoteViewState) => void;
	userCanUpdateNote: boolean;
	openNoteCreation: () => void;
};

export const ArchivedPageBannerViewNoteActions: FC<ArchivedPageBannerNoteActionsProps> = ({
	noteViewState,
	setNoteViewState,
	userCanUpdateNote,
	openNoteCreation,
}) => {
	const expandNote = useCallback(() => {
		setNoteViewState(NoteViewState.EXPANDED);
	}, [setNoteViewState]);

	const collapseNote = useCallback(() => {
		setNoteViewState(NoteViewState.COLLAPSED);
	}, [setNoteViewState]);

	const getExpandCollapseButton = () => {
		if (noteViewState !== NoteViewState.CONTENT_FITS) {
			const [handleClick, buttonText] =
				noteViewState === NoteViewState.COLLAPSED
					? [expandNote, i18n.showMore]
					: [collapseNote, i18n.showLess];

			return (
				<ArchivePageActionLink onClick={handleClick}>
					<FormattedMessage {...buttonText} />
				</ArchivePageActionLink>
			);
		}

		return null;
	};

	const getEditNoteButton = () => {
		if (userCanUpdateNote) {
			return (
				<ArchivePageActionLink onClick={openNoteCreation}>
					<FormattedMessage {...i18n.editNote} />
				</ArchivePageActionLink>
			);
		}

		return null;
	};

	const expandCollapseButton = getExpandCollapseButton();
	const editNoteButton = getEditNoteButton();

	if (!expandCollapseButton && !editNoteButton) {
		return null;
	}

	return (
		<NoteActionsWrapper>
			{expandCollapseButton}
			{expandCollapseButton && editNoteButton && <HorizontalSeparator />}
			{editNoteButton}
		</NoteActionsWrapper>
	);
};
